// external
import type { FC } from 'react';
import { useSelector } from 'react-redux';

// types
import type { paymentServicesTypes } from '@makemydeal/dr-platform-types';
import { TaxClasses } from '@makemydeal/dr-platform-types';

// store
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import * as taxUtil from '@makemydeal/dr-dash-store/utils/taxUtils';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';

// constants
import {
    CAPITALIZED_TAXES_CATEGORY,
    MONTHLY_TAXES_CATEGORY,
    MONTHLY_USE_TAXES_CATEGORY,
    UPFRONT_TAXES_CATEGORY
} from './../constants';

// custom component
import TaxCategory from './TaxCategory';

type ItemizedLeaseSummaryProps = {
    data: paymentServicesTypes.TaxItems;
    taxOverrideEnabled?: boolean;
};

const ItemizedLeaseSummary: FC<ItemizedLeaseSummaryProps> = ({ data, taxOverrideEnabled }) => {
    const isTaxCapped = useSelector(offerReduxSelectors.isTaxCapped);
    const isCrtCapped = useSelector(offerReduxSelectors.getCapReductionTaxCapitalizedIndicator);
    const isCTPropertyTaxEnabled = useSelector(featureToggleSelectors.isCTPropertyTaxCalculationEnabled);

    const propertyTaxItems = isCTPropertyTaxEnabled
        ? data.filter((taxItem) => taxItem.class === TaxClasses.ADDITIONAL && taxItem.monthlyTax != null)
        : [];
    const showPropertyTax = propertyTaxItems.length > 0;

    const taxItemsToCategorize = showPropertyTax ? data.filter((taxItem) => !propertyTaxItems.includes(taxItem)) : data;
    const { capitalizedTaxes, monthlyUseTaxes, upfrontTaxes } = taxUtil.categorizeTaxes(taxItemsToCategorize, {
        isTaxCapped,
        isCrtCapped
    });

    const monthlyTaxItems = showPropertyTax ? [...(monthlyUseTaxes ?? []), ...propertyTaxItems] : monthlyUseTaxes;

    return (
        <>
            <TaxCategory
                categoryTitle={CAPITALIZED_TAXES_CATEGORY}
                taxItems={capitalizedTaxes}
                dataTestId="category_capitalized_taxes"
                canToggleUpfrontAllTaxes={!taxOverrideEnabled && isTaxCapped}
                taxOverrideEnabled={taxOverrideEnabled}
            />
            <TaxCategory
                categoryTitle={UPFRONT_TAXES_CATEGORY}
                taxItems={upfrontTaxes}
                dataTestId="category_upfront_taxes"
                canToggleUpfrontAllTaxes={!taxOverrideEnabled && !isTaxCapped}
                taxOverrideEnabled={taxOverrideEnabled}
            />
            <TaxCategory
                categoryTitle={showPropertyTax ? MONTHLY_TAXES_CATEGORY : MONTHLY_USE_TAXES_CATEGORY}
                taxItems={monthlyTaxItems}
                dataTestId="category_monthly_use_taxes"
                hideTaxNames={!showPropertyTax}
                taxOverrideEnabled={taxOverrideEnabled}
            />
        </>
    );
};

export default ItemizedLeaseSummary;
